import { QueryClient } from '@tanstack/react-query'

import config from '@config'
import { FeatureFlagsEntries, getABTestValue } from '@hooks/useABTestValue'

import {
  queryKey as chunksQueryKey,
  createQuery as createChunksQuery,
} from './queries/chunks'
import {
  createQuery as createEmbeddedVideosQuery,
  queryKey as embeddedVideosQueryKey,
} from './queries/embeddedVideos'
import {
  createQuery as createPolarisBitesQuery,
  queryKey as polarisBitesQueryKey,
} from './queries/polarisBites'
import {
  createQuery as createPolarisPlusContentQuery,
  queryKey as polarisPlusContentQueryKey,
} from './queries/polarisPlusContent'
import {
  createQuery as createPolarisPopularQuery,
  queryKey as polarisPopularQueryKey,
} from './queries/polarisPopular'
import {
  createQuery as createPolarisViralVideosQuery,
  queryKey as polarisViralVideosQueryKey,
} from './queries/polarisViralVideos'
import {
  createQuery as createRecommendedContentQuery,
  queryKey as recommendedContentQueryKey,
} from './queries/recommendedContent'

const queryKeys = [
  chunksQueryKey,
  embeddedVideosQueryKey,
  polarisBitesQueryKey,
  polarisPlusContentQueryKey,
  polarisPopularQueryKey,
  polarisViralVideosQueryKey,
  recommendedContentQueryKey,
] as const

type AvailableQueryKeys = (typeof queryKeys)[number]

const queriesInfo = {
  [chunksQueryKey]: {
    createQuery: createChunksQuery,
  },
  [embeddedVideosQueryKey]: {
    createQuery: createEmbeddedVideosQuery,
  },
  [polarisBitesQueryKey]: {
    createQuery: createPolarisBitesQuery,
  },
  [polarisPlusContentQueryKey]: {
    createQuery: createPolarisPlusContentQuery,
  },
  [polarisPopularQueryKey]: {
    createQuery: createPolarisPopularQuery,
  },
  [polarisViralVideosQueryKey]: {
    createQuery: createPolarisViralVideosQuery,
  },
  [recommendedContentQueryKey]: {
    createQuery: createRecommendedContentQuery,
  },
} as const satisfies {
  [K in AvailableQueryKeys]: { createQuery: QueryFactory<K> }
}

declare global {
  interface Window {
    AureusClient: new (options: { config: Record<string, unknown> }) => any
    aureusClient: {
      sendDeboostingEvents: (
        itemsToDeboost: {
          offerId: string
          contentId: string
        }[],
        strategy: string,
      ) => void
      getBatchRecommendations: <
        K extends AvailableQueryKeys,
        T extends Record<string, unknown>,
        D = AureusBatchRecommendationsResponse<K, T>,
      >(
        query: ReturnType<(typeof queriesInfo)[K]['createQuery']>,
        callback?: (error: Error | null, data?: D) => void,
        options?: Record<string, unknown>,
      ) => Promise<D>
    }
  }
}

const {
  aureus: { aureusEnvironment },
} = config

export type Config = {
  publication?: string
  aureusEnvironment: string
}

export type AureusEnvironmentConfig = {
  aureusEnvironment: string
}

export type PublicationConfig = {
  publication: string
  aureusEnvironment: string
}

const DEBOOSTING_STRATEGY = {
  CLICK: 'click',
  VIEWABILITY: 'viewability',
  VIDEO_VIEWS: 'video_views',
} as const

type DeboostingStrategyKeys = keyof typeof DEBOOSTING_STRATEGY
type DeboostingStrategyValues =
  (typeof DEBOOSTING_STRATEGY)[DeboostingStrategyKeys]

type TriggerAureusImpression = (args: {
  aureusOfferId: string | undefined
  queryClient: QueryClient
}) => void

type TriggerAureusClick = (args: {
  url: string
  aureusOfferId: string
  articleId: string
  sectionId: string
}) => void

type TriggerAureusDeboosting = (args: {
  itemsToDeboost: { offerId: string; contentId: string }[]
  strategy?: DeboostingStrategyValues
}) => void

export type AureusBatchRecommendationsResponse<
  K extends AvailableQueryKeys,
  T extends Record<string, unknown>,
> = {
  results: {
    [queryName in K]?: { recommendations?: T[] }
  }
}

export type AureusQuery<K extends AvailableQueryKeys> = {
  [key in K]: {
    resultsLimit: number
    pinningOffset?: number
    context: {
      section: string
      purpose: Purpose
      minHardness?: number
      contentBlacklist?: string
      contentId?: string
      currentSession?: {
        deboosted: string[]
      }
    }
  }
}

export type QueryFactory<K extends AvailableQueryKeys> = (
  args: any,
) => AureusQuery<K>

const PURPOSE_VALUES = ['test', 'control', 'reference'] as const

export type Purpose = (typeof PURPOSE_VALUES)[number]

const isValidPurpose = (value: any): value is Purpose =>
  PURPOSE_VALUES.includes(value)

/**
 * @param abTestKey - The key of the AB test as set in Growthbook
 * @param queryClient - The query client
 * @returns The purpose for the Aureus recommendations query
 * The purpose should have one of the following values as set also in Growthbook:
 * - If there is no AB test running, the purpose will be 'reference'
 * - When an experiment is running, we will send 'test' in any variants that affect the logic
 * of the Aureus recommendations. Otherwise, we will send 'control'.
 */
const getAureusPurpose = (
  abTestKey?: keyof FeatureFlagsEntries,
  queryClient?: QueryClient,
): Purpose => {
  if (!abTestKey || !queryClient) {
    return 'reference'
  }

  const flagValue = getABTestValue(abTestKey, queryClient)

  return isValidPurpose(flagValue) ? flagValue : 'reference'
}

const getSectionWithAureusEnvironment = <T extends string>(section: T) =>
  `${section}-${aureusEnvironment}` as const

const triggerAureusImpression: TriggerAureusImpression = ({
  aureusOfferId,
  queryClient,
}) => {
  if (!aureusOfferId) {
    return
  }

  //! We should only track one impression per offerId (per Page)
  //! Resetting the impressions so it triggers on a new page happens
  //! in getServerSideProps().
  if (
    (queryClient.getQueryData<Record<string, unknown>>([
      'aureusOfferTracked',
    ]) ?? {})[aureusOfferId]
  ) {
    return
  }

  queryClient.setQueryData(['aureusOfferTracked'], {
    ...(queryClient.getQueryData<Record<string, unknown>>([
      'aureusOfferTracked',
    ]) ?? {}),
    [aureusOfferId]: true,
  })

  window.dlApi.cmd.push((dlApi: any) => {
    dlApi.registerAureusOffers([aureusOfferId])
  })
}

const triggerAureusClick: TriggerAureusClick = ({
  articleId,
  url,
  aureusOfferId,
  sectionId,
}) => {
  if (!window.dlApi || !url || !sectionId || !aureusOfferId || !articleId) {
    return
  }

  window.dlApi.cmd.push((dlApi: any) => {
    dlApi.clkEvent?.(
      url,
      'keep watching',
      sectionId,
      '',
      '',
      '',
      '',
      '',
      aureusOfferId,
      articleId,
      '',
    )
  })
}

const triggerAureusDeboosting: TriggerAureusDeboosting = ({
  strategy = DEBOOSTING_STRATEGY.CLICK,
  itemsToDeboost,
}) => {
  if (!itemsToDeboost?.length) {
    return
  }

  window.aureusClient?.sendDeboostingEvents(itemsToDeboost, strategy)
}

const getAureusBatchRecommendations = <
  K extends AvailableQueryKeys,
  T extends Record<string, unknown>,
  D = AureusBatchRecommendationsResponse<K, T>,
>(
  query: ReturnType<(typeof queriesInfo)[K]['createQuery']>,
  options?: Record<string, unknown>,
) =>
  new Promise<D>((resolve, reject) => {
    if (!window.aureusClient) {
      reject(new Error('Aureus Client is not available'))
    }

    window.aureusClient.getBatchRecommendations<K, T, D>(
      query,
      (error, data) => {
        if (error) {
          reject(error)
          return
        }

        if (!data) {
          reject(new Error('No data received'))
          return
        }

        resolve(data)
      },
      options,
    )
  })

export type ExecuteQuery<K extends AvailableQueryKeys> = (
  queryOptions: Parameters<(typeof queriesInfo)[K]['createQuery']>[0],
  options?: Record<string, unknown>,
) => Promise<
  | NonNullable<
      NonNullable<
        Awaited<
          ReturnType<
            typeof getAureusBatchRecommendations<K, Record<string, unknown>>
          >
        >['results'][K]
      >['recommendations']
    >
  | undefined
>

export {
  queryKeys,
  DEBOOSTING_STRATEGY,
  getSectionWithAureusEnvironment,
  triggerAureusImpression,
  triggerAureusClick,
  triggerAureusDeboosting,
  getAureusBatchRecommendations,
  getAureusPurpose,
}
